import { useEffect, useState } from 'react'
import { CalendarCheck } from '@phosphor-icons/react'
import { useAuth } from '../auth'
import { formatApiError, listCheckins, recordCheckinOutcome, type Checkin, type CheckinVerdict } from '../api'
import { analytics } from '../lib/analytics'
import { cn, pct } from '../lib/utils'

const VERDICTS: { value: CheckinVerdict; label: string }[] = [
  { value: 'better', label: 'Better than expected' },
  { value: 'as_expected', label: 'About as expected' },
  { value: 'worse', label: 'Worse than expected' },
]
const VERDICT_LABEL: Record<CheckinVerdict, string> = {
  better: 'better than expected', as_expected: 'about as expected', worse: 'worse than expected',
}

const said = (c: Checkin) => `${c.expected.winner} came out ahead in ${pct(c.expected.p_better)} of futures`

/**
 * Dashboard "Check-ins" (profile-concept §5.3): the decision journal. Due
 * check-ins ask what you chose and how it went, next to what the result said;
 * recorded ones show both side by side. Anchored at #checkins (email link).
 */
export default function CheckinsSection() {
  const auth = useAuth()
  const [items, setItems] = useState<Checkin[]>([])

  useEffect(() => {
    if (!auth.user) return
    listCheckins().then(setItems).catch(() => setItems([]))
  }, [auth.user])

  if (!auth.user || items.length === 0) return null
  const now = Date.now()
  const due = items.filter((c) => !c.recorded_at && new Date(c.due_at).getTime() <= now)
  const upcoming = items.filter((c) => !c.recorded_at && new Date(c.due_at).getTime() > now)
  const recorded = items.filter((c) => c.recorded_at)
  const replace = (c: Checkin) => setItems((xs) => xs.map((x) => (x.decision_id === c.decision_id ? c : x)))

  return (
    <section id="checkins" className="space-y-3 scroll-mt-24" data-testid="checkins-section">
      <h2 className="font-heading text-2xl font-bold inline-flex items-center gap-2">
        <CalendarCheck size={24} weight="fill" className="text-primary-ink" /> Check-ins
      </h2>
      {due.map((c) => <DueCheckin key={c.decision_id} checkin={c} onRecorded={replace} />)}
      {upcoming.length > 0 && (
        <ul className="text-sm space-y-1" data-testid="checkins-upcoming">
          {upcoming.map((c) => (
            <li key={c.decision_id} className="text-ink-muted">
              <strong className="text-ink">{c.decision_name}</strong>: check-in on {new Date(c.due_at).toLocaleDateString()}
            </li>
          ))}
        </ul>
      )}
      {recorded.length > 0 && (
        <ul className="space-y-2" data-testid="checkins-recorded">
          {recorded.map((c) => (
            <li key={c.decision_id} className="rounded-xl border-2 border-edge bg-surface p-3 text-sm">
              <div className="font-bold">{c.decision_name}</div>
              <div className="text-ink-muted">Expected: {said(c)}.</div>
              <div>
                You chose <strong>{c.chosen}</strong>; it went{' '}
                <strong>{c.verdict ? VERDICT_LABEL[c.verdict] : ''}</strong>.
                {c.note ? <span className="text-ink-muted"> "{c.note}"</span> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function DueCheckin({ checkin, onRecorded }: { checkin: Checkin; onRecorded: (c: Checkin) => void }) {
  const options = checkin.expected.options?.length ? checkin.expected.options : [checkin.expected.winner]
  const [chosen, setChosen] = useState(options[0])
  const [verdict, setVerdict] = useState<CheckinVerdict | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async () => {
    if (!verdict) return
    setBusy(true)
    setErr(null)
    try {
      const c = await recordCheckinOutcome(checkin.decision_id, { chosen, verdict, note: note.trim() || undefined })
      onRecorded(c)
      analytics.track('checkin_recorded', { verdict, followed_winner: chosen === checkin.expected.winner })
    } catch (e: any) {
      setErr(e?.response?.data?.detail ? formatApiError(e.response.data.detail) : e?.message || 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card-brutal p-4 sm:p-5 space-y-3 border-primary" data-testid="checkin-due">
      <div>
        <div className="font-heading font-bold text-lg">How did "{checkin.decision_name}" go?</div>
        <div className="text-sm text-ink-muted">When you modelled it, {said(checkin)}.</div>
      </div>
      <label className="block text-sm">
        <span className="label-bold block mb-1">What did you choose?</span>
        <select value={chosen} onChange={(e) => setChosen(e.target.value)} className="input-brutal w-full" data-testid="checkin-chosen">
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
      <div className="text-sm">
        <span className="label-bold block mb-1">How is it going?</span>
        <div className="flex flex-wrap gap-2">
          {VERDICTS.map((v) => (
            <button
              key={v.value}
              onClick={() => setVerdict(v.value)}
              className={cn('px-3 py-1.5 rounded-xl border-2 border-edge font-bold text-sm',
                verdict === v.value ? 'bg-secondary text-white shadow-brutal-sm' : 'hover:bg-muted')}
              data-testid={`checkin-verdict-${v.value}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Anything worth remembering? (optional)"
        maxLength={500}
        className="input-brutal w-full text-sm"
        data-testid="checkin-note"
      />
      {err && <p className="text-xs text-primary-ink">{err}</p>}
      <button onClick={save} disabled={!verdict || busy} className="btn-primary text-sm" data-testid="checkin-save">
        Save check-in
      </button>
    </div>
  )
}
